import TokenExpiredComponent from '@app/components/shared/tokenExpiredComponent';
import AlertPopUp from '@app/components/core/alert-popup';
import Layout from '@app/components/core/layout';
import { maxWidthMobile } from '@app/constants/app';
import {
  clientReportingRoutePrefixPattern,
  clientReportingSlugPath,
  forgotPasswordPath,
  resetPasswordPath,
} from '@app/constants/url';
import { ScreenSizeProvider } from '@app/utils/context';
import { isAuthRefreshToken, searchWithoutToken } from '@app/utils/helpers/auth-refresh-token';
import ErrorBoundary from '@app/utils/error-boundary';
import { DecodedJwt, useGetUserData, useRefreshToken, UserEntity, useResendSprintProduct } from '@data';
import { useOrganizations } from '@data/hooks/organizations';
import { useStore } from '@data/storages';
import { apiStore } from '@data/store';
import { jwtDecode } from 'jwt-decode';
import React, { useCallback, useEffect, useState } from 'react';
import { hot } from 'react-hot-loader/root';
import { matchPath, useLocation, useNavigate } from 'react-router-dom';
import { Loader } from '@ui';
import { ForgotPasswordPage, LoginPage, ResetPasswordPage } from '@app/components/core';
import { readAccessToken, REFRESH_TOKEN_STORAGE_KEY } from '@app/constants/app/storage';
import App from './pages';

const mql = window.matchMedia(`(max-width: ${maxWidthMobile})`);

const PanencoBot = () => {
  const [isMobile, setIsMobile] = useState(mql.matches);
  const [isPopUpBlocked, setPopUpIsBlocked] = useState(false);

  const { mutate: getUserData } = useGetUserData();
  const [accessToken, setAccessToken] = useState(readAccessToken());
  const refreshToken = new URLSearchParams(window.location.search)?.get('token');
  const navigate = useNavigate();
  const location = useLocation();
  const [isRefreshFailed, setRefreshFailed] = useState<boolean | undefined>(false);
  const { mutate: resendSprint } = useResendSprintProduct();
  const { tokenData, setTokenData } = useStore();
  const { mutate: refresh } = useRefreshToken();

  const clientUrlPrefixMatch = matchPath({ path: clientReportingRoutePrefixPattern, end: false }, location.pathname);
  const clientUrlExactMatch = matchPath({ path: clientReportingSlugPath, end: true }, location.pathname);

  const handleRefresh = useCallback(() => {
    const { refreshFailed } = apiStore.getState();
    setRefreshFailed(refreshFailed);
  }, []);

  useEffect(() => {
    const unsubFromRefresh = apiStore.subscribe(handleRefresh, state => state.refreshFailed);
    return () => {
      unsubFromRefresh();
      apiStore.destroy();
    };
  }, []);

  const handleMqlChange = () => {
    setIsMobile(mql.matches);
  };

  useEffect(() => {
    // For Safari < 14
    if (typeof mql.addListener === 'function') {
      mql.addListener(handleMqlChange);
    } else {
      mql.addEventListener('change', handleMqlChange);
    }

    return () => {
      // For Safari < 14
      if (typeof mql.removeListener === 'function') {
        mql.removeListener(handleMqlChange);
      } else {
        mql.removeEventListener('change', handleMqlChange);
      }
    };
  }, []);

  useEffect(() => {
    // Hooks run before the early returns below, so this fired on the reset-password route too, where
    // `?token` is the reset token from the email rather than an OAuth one. See isAuthRefreshToken.
    if (isAuthRefreshToken(location.pathname, refreshToken)) {
      localStorage.setItem(REFRESH_TOKEN_STORAGE_KEY, refreshToken);
      refresh(
        { token: refreshToken },
        {
          onSuccess: () => {
            setAccessToken(readAccessToken());
            // Only the token goes: the rest is read by the page behind this loader, on its first render.
            navigate({ search: searchWithoutToken(window.location.search) }, { replace: true });
          },
        },
      );
    }
  }, [refreshToken, navigate, refresh, location.pathname]);
  let userData: UserEntity | undefined;

  if (accessToken) {
    const decodedJwt = jwtDecode<DecodedJwt>(accessToken);
    userData = { ...decodedJwt, id: decodedJwt.userId };
    if (!tokenData) {
      setTokenData(decodedJwt);
    }
  }

  const { data: organizations } = useOrganizations(userData?.id);

  // Ahead of the session check on purpose: a reset link has to open the same screen whether or not
  // the browser still holds a token from an earlier sign-in.
  if (location.pathname === forgotPasswordPath) {
    return <ForgotPasswordPage />;
  }
  if (location.pathname === resetPasswordPath) {
    return <ResetPasswordPage />;
  }

  if (!accessToken && isPopUpBlocked && !clientUrlPrefixMatch)
    return <AlertPopUp handleClick={() => setPopUpIsBlocked(false)} />;

  if (isRefreshFailed && clientUrlExactMatch) {
    const { productId, sprintId } = clientUrlExactMatch.params;
    return <TokenExpiredComponent sendMails={() => resendSprint({ productId: productId!, sprintId: sprintId! })} />;
  }

  if (!accessToken) {
    // A `token` in the URL (a client reporting link, or an employee refresh) is being exchanged for
    // an access token. Show the loader rather than flashing the sign-in page during that hop.
    if (refreshToken) {
      return <Loader />;
    }
    return (
      <LoginPage
        onGoogleCredential={credential =>
          getUserData(
            { token: credential },
            {
              onSuccess: () => {
                setAccessToken(readAccessToken());
              },
            },
          )
        }
        onGoogleError={() => setPopUpIsBlocked(true)}
        onLoginSuccess={() => {
          setAccessToken(readAccessToken());
        }}
      />
    );
  }

  if (!userData || !organizations) {
    return <Loader />;
  }

  return (
    <ScreenSizeProvider value={{ isMobile }}>
      {userData ? (
        <div className="flex min-h-0 w-full flex-1 flex-col">
          <Layout user={userData} organizations={organizations?.items}>
            <ErrorBoundary>
              <App user={userData} />
            </ErrorBoundary>
          </Layout>
        </div>
      ) : null}
    </ScreenSizeProvider>
  );
};

export default hot(PanencoBot);
